// Rubros / conceptos del rol de pagos (nomina.rubros). Nómina → Configuración.
// Los rubros del sistema (es_sistema) los usa el motor del rol: no se eliminan
// ni cambian de código/tipo (trigger en BD); sí se puede editar nombre y flags.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { Campo, Modal, Switch, fieldCls, nom, useDialogoEnLinea, type Rubro } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

const vacio = { codigo: '', nombre: '', tipo: 'ingreso' as 'ingreso' | 'egreso', afecta_iess: true, afecta_ir: true, es_activo: true };

export function Rubros() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Rubro[]>({
    queryKey: ['nom-rubros'],
    queryFn: () => nom.list<Rubro>('rubros', '?select=*&deleted_at=is.null&order=tipo,codigo'),
  });
  const [editando, setEditando] = useState<Rubro | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (r?: Rubro) => {
    if (r) setForm({ codigo: r.codigo, nombre: r.nombre, tipo: r.tipo, afecta_iess: r.afecta_iess, afecta_ir: r.afecta_ir, es_activo: r.es_activo });
    else setForm(vacio);
    setEditando(r ?? null); setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = editando?.es_sistema
        ? { nombre: form.nombre, afecta_iess: form.afecta_iess, afecta_ir: form.afecta_ir, es_activo: form.es_activo }
        : { ...form };
      return editando ? nom.update('rubros', editando.id, body) : nom.create('rubros', body);
    },
    onSuccess: () => { toast.success('Rubro guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-rubros'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => nom.update('rubros', id, { deleted_at: new Date().toISOString(), es_activo: false }),
    onSuccess: () => { toast.success('Rubro eliminado'); void qc.invalidateQueries({ queryKey: ['nom-rubros'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const Marca = ({ v }: { v: boolean }) => <span className={v ? 'text-estable' : 'text-muted-foreground'}>{v ? 'Sí' : 'No'}</span>;

  const headersExp = ['N°', 'Código', 'Nombre', 'Tipo', 'IESS', 'IR', 'Estado'];
  const filasExport: Celda[][] = rows.map((r, i): Celda[] => [
    i + 1, r.codigo, r.nombre, r.tipo, r.afecta_iess ? 'Sí' : 'No', r.afecta_ir ? 'Sí' : 'No', r.es_activo ? 'Activo' : 'Inactivo',
  ]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Rubros y conceptos</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`rubros-${fechaLocal()}`}
            titulo="Rubros y conceptos" headers={headersExp} filas={filasExport} colsDerecha={[0]}
            disabled={rows.length === 0}
          />
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo rubro</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Rubros ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Tipo</th>
              <th className="py-2 pr-3">IESS</th><th className="py-2 pr-3">IR</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">
                    {r.codigo}{r.es_sistema && <Lock className="w-3 h-3 inline ml-1 text-muted-foreground" aria-label="Rubro del sistema" />}
                  </td>
                  <td className="py-2 pr-3">{r.nombre}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${r.tipo === 'ingreso' ? 'bg-estable-soft text-estable' : 'bg-urgente-soft text-urgente'}`}>{r.tipo}</span>
                  </td>
                  <td className="py-2 pr-3"><Marca v={r.afecta_iess} /></td>
                  <td className="py-2 pr-3"><Marca v={r.afecta_ir} /></td>
                  <td className="py-2 pr-3">{r.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(r)}><Pencil className="w-4 h-4" /></button>
                    {!r.es_sistema && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${r.nombre}"?`, { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(r.id); }}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editando ? `Editar rubro ${editando.codigo}` : 'Nuevo rubro'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            {editando?.es_sistema && <div className="text-xs text-pendiente">Rubro del sistema: el código y el tipo no se pueden cambiar.</div>}
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código *"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} disabled={!!editando?.es_sistema} /></Campo>
              <Campo label="Tipo *">
                <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as 'ingreso' | 'egreso' })} disabled={!!editando?.es_sistema}>
                  <option value="ingreso">Ingreso</option><option value="egreso">Egreso</option>
                </select>
              </Campo>
            </div>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <div className="grid grid-cols-3 gap-3">
              <Switch checked={form.afecta_iess} onChange={(v) => setForm({ ...form, afecta_iess: v })} label="Materia gravada IESS" />
              <Switch checked={form.afecta_ir} onChange={(v) => setForm({ ...form, afecta_ir: v })} label="Base para IR" />
              <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.codigo.trim() || !form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
